import { Box, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { VyTooltip } from '../../VyTooltip';
import { StatusLabel } from './StatusLabel';

type VyStatusSelectProps<TStatus extends string> = {
  value: TStatus;
  onChange: (event: SelectChangeEvent<TStatus>) => void;
  options: TStatus[];
  disabled?: boolean;
  colorsConfig: Record<TStatus, { backgroundColor: string; color: string }>;
  disabledTooltipText?: string;
  getIsOptionDisabled?: (status: TStatus) => boolean;
  getDisplayLabel?: (status: TStatus) => string;
};

export const VyStatusSelect = <TStatus extends string>({
  value,
  onChange,
  disabled,
  options,
  colorsConfig,
  getIsOptionDisabled,
  getDisplayLabel,
  disabledTooltipText,
}: VyStatusSelectProps<TStatus>) => {
  return (
    <Box
      onClick={(event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        event.preventDefault();
      }}
      sx={{
        width: '100%',
      }}
    >
      <Select
        value={value}
        onChange={onChange}
        displayEmpty
        disableUnderline
        disabled={disabled}
        size="small"
        sx={{
          width: '100%',
          '& .MuiSelect-icon': {
            color: 'black',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
        }}
        renderValue={(selectedStatus) => {
          const { backgroundColor, color } = colorsConfig[selectedStatus];
          const displayLabel = getDisplayLabel?.(selectedStatus) ?? selectedStatus;

          return (
            <StatusLabel
              label={displayLabel}
              backgroundColor={backgroundColor}
              color={color}
              sx={{ width: '100%' }}
            />
          );
        }}
      >
        {options.map((status) => {
          const { backgroundColor, color } = colorsConfig[status];
          const isDisabled = getIsOptionDisabled?.(status) ?? false;

          const menuItem = (
            <MenuItem value={status} key={status}>
              <StatusLabel
                label={getDisplayLabel?.(status) ?? status}
                backgroundColor={backgroundColor}
                color={color}
                sx={{
                  width: '100%',
                }}
              />
            </MenuItem>
          );

          return isDisabled ? (
            <VyTooltip key={status} tooltipContent={disabledTooltipText} placement="right-end">
              <Box>{menuItem}</Box>
            </VyTooltip>
          ) : (
            menuItem
          );
        })}
      </Select>
    </Box>
  );
};
